<script lang="ts">
	/**
	 * The purchase funnel (§12.10) — the single most useful thing on the analytics page, because the
	 * stage a store is losing people at is the only one worth doing anything about.
	 *
	 * **Hand-drawn rather than a library form.** A funnel is a bar chart whose bars are ordered and
	 * whose interesting quantity is the *gap between neighbours*, and no charting default draws that
	 * gap. Here it is drawn: each rung's **track is the step before it** and the fill is what
	 * survived, so the exposed part of the track is the loss itself rather than something inferred
	 * by comparing two lengths. The numbers stay beside it in words, which is also the relief the
	 * light-mode palette obliges — nothing here rests on hue.
	 *
	 * **The classic trapezoid was rejected.** It is the iconic shape and it reads worse: it encodes
	 * with area and slope, which are measurably harder to compare than aligned lengths, and it has
	 * nowhere to put a per-step label without crowding. Aligned bars are the base; what was missing
	 * was never the silhouette, it was the loss.
	 *
	 * **One hue for every stage.** Position already encodes the stage, and the bars are one series on
	 * one scale; a colour per stage would spend the categorical palette saying what the order says.
	 *
	 * The bars are proportional to the **first** stage rather than to the widest, so a funnel that
	 * loses ninety per cent at the first step looks like one. Normalising to the largest bar would
	 * redraw every funnel as the same shape. Nesting the fill inside the track keeps both readings
	 * true at once: its width against the page is still this stage as a share of the first, so rungs
	 * stay comparable top to bottom, while its width against its own track is the step conversion.
	 *
	 * The biggest drop is named, and by **absolute** loss rather than by rate: a late step routinely
	 * has the worst percentage — 58% of a hundred is a smaller problem than 50% of six hundred — so
	 * ranking by rate points at the bottom of the funnel every time, which is true and useless.
	 */

	import type { AnalyticsFunnelStage } from '../../AppTypes';
	import { chartTheme } from './chartTheme';

	export let stages: AnalyticsFunnelStage[] = [];

	$: theme = chartTheme();
	$: top = stages[0]?.sessions ?? 0;

	/**
	 * How wide a rung's **track** is: the previous stage, as a share of the first.
	 *
	 * The track is not chrome here, it is the other half of the data. Every bar used to start at the
	 * same left edge inside a full-width groove, so the only thing on screen was what *remained* and
	 * the loss at each step was something the reader inferred by comparing two lengths. Sizing the
	 * track to the step before turns the leftover into the drop itself — the question a funnel is
	 * actually read to answer, drawn rather than described.
	 */
	function fnTrackWidth(index: number): number {
		if (top <= 0) return 0;
		const previous = index === 0 ? top : (stages[index - 1]?.sessions ?? 0);
		return (previous / top) * 100;
	}

	/**
	 * How much of that track is retained — which is exactly this step's conversion.
	 *
	 * Nesting the fill inside the track is what keeps both readings true at once: the fill's width
	 * against the *page* is still this stage as a share of the first, so rungs remain comparable
	 * top to bottom, while its width against its own track is the step conversion. One element,
	 * two honest comparisons, no second scale to explain.
	 */
	function fnFillWidth(index: number): number {
		const previous = index === 0 ? top : (stages[index - 1]?.sessions ?? 0);
		if (previous <= 0) return 0;
		const sessions = stages[index]?.sessions ?? 0;
		// A rung with sessions but a hairline width is still a rung that happened; zero stays zero.
		return sessions === 0 ? 0 : Math.max((sessions / previous) * 100, 1.5);
	}

	/**
	 * The step that loses the most people, by **absolute** loss rather than by rate.
	 *
	 * A late step routinely has the worst percentage — 58% of a hundred is a smaller problem than
	 * 50% of six hundred — so ranking by rate points at the bottom of the funnel every time, which
	 * is both true and useless. What an operator can act on is where the most customers go, and
	 * naming it is the difference between a chart you read and a chart you have to study. Null when
	 * nothing has dropped, because "biggest drop" over an empty funnel is a claim about nothing.
	 */
	$: worstIndex = (() => {
		let worst = -1;
		let lost = 0;
		stages.forEach((stage, index) => {
			if (index > 0 && stage.dropOff > lost) {
				lost = stage.dropOff;
				worst = index;
			}
		});
		return lost > 0 ? worst : null;
	})();

	function fnPercent(fraction: number): string {
		return `${(fraction * 100).toFixed(fraction < 0.1 ? 1 : 0)}%`;
	}

</script>

{#if stages.length === 0}
	<p class="text-default text-sm">No visits in this range.</p>
{:else}
	<ol class="space-y-2" aria-label="Purchase funnel">
		{#each stages as stage, index (stage.key)}
			<li>
				<div class="flex items-baseline justify-between gap-3 text-sm">
					<span class="font-medium">{stage.label}</span>
					<span class="tabular-nums font-semibold">{stage.sessions.toLocaleString()}</span>
				</div>

				<!--
					The track is the step before and the fill is what survived it, so the exposed part
					**is** the loss. An inline width rather than a Tailwind class: a class built at
					runtime is invisible to the JIT scanner and the rule silently never ships, which is
					the same reason `rungColor` returns a hex.
				-->
				<div class="mt-1 h-3">
					<div
						class="h-full rounded-full bg-gray-200 dark:bg-zinc-700 overflow-hidden transition-[width] duration-300"
						style={`width: ${fnTrackWidth(index)}%;`}
						role="img"
						aria-label={index === 0
							? `${stage.label}: ${stage.sessions} visits`
							: `${stage.label}: ${stage.sessions} visits, ${stage.dropOff} lost at this step`}
					>
						<div
							class="h-full rounded-full transition-[width] duration-300"
							style={`width: ${fnFillWidth(index)}%; background-color: ${theme.single};`}
						></div>
					</div>
				</div>

				{#if index > 0}
					<p class="text-default text-xs mt-1 tabular-nums">
						{fnPercent(stage.conversionFromPrevious)} of the step before ·
						{stage.dropOff.toLocaleString()} lost here
						{#if index === worstIndex}
							<!-- Named, not merely coloured: the largest drop is the one thing on this
							     panel somebody can act on, and colour alone says it to nobody using a
							     screen reader or printing the page. -->
							<span class="text-amber-600 dark:text-amber-400 font-medium">
								· biggest drop
							</span>
						{/if}
					</p>
				{/if}
			</li>
		{/each}
	</ol>
{/if}
